import { useState, useEffect } from 'react';
import { Box, Dialog } from '@mui/material';
import { StationStatusType, UpdateStationRequest, Station } from '@/types/azk-list.types';

import {
  useGetAzkListFormOptionsQuery,
  useGetStationProfileQuery,
  useUpdateStationProfileMutation,
  useDeleteStationMutation
} from '../../../services';
import { useSnackbar } from '../../../hooks';

import {
  ProfileMap,
  ProfileTags,
  ProfileForm,
  ProfileHeader,
  ProfileActions,
  generateUnicode,
  isOkkoStationCheck,
  DeleteConfirmDialog
} from './components';
import { StationProfileDialogProps } from './StationProfile.types';
import { findId, getStatusKey, requiredFields } from './utils';
import './StationProfile.css';

/**
 * Station profile dialog component for viewing and editing station details
 * Provides comprehensive station management with form validation and data persistence
 *
 * Features:
 * - View/edit mode toggle with field change tracking
 * - Form validation and error handling
 * - Station deletion with confirmation dialog
 * - Google Maps integration for location display
 * - Support for both OKKO and competitor stations
 * - Real-time field change detection
 * - Automatic data loading and form option population
 *

 */
function StationProfile({ open, station, readOnly = false, onClose }: StationProfileDialogProps) {
  const { data: options } = useGetAzkListFormOptionsQuery();
  const { data: stationProfile, isLoading: isStationProfileLoading } = useGetStationProfileQuery(
    String(station.unicode),
    {
      skip: !open || !station.unicode
    }
  );

  const [updateStationProfile] = useUpdateStationProfileMutation();
  const [deleteStation] = useDeleteStationMutation();
  const { setSnackbarMessage } = useSnackbar();
  const [editedStation, setEditedStation] = useState<Station | null>(null);
  const [originalStation, setOriginalStation] = useState<Station | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [regions, setRegions] = useState<{ id: number; name: string }[]>([]);
  const [provinces, setProvinces] = useState<{ id: number; name: string }[]>([]);
  const [divisions, setDivisions] = useState<{ id: number; name: string }[]>([]);
  const [brands, setBrands] = useState<{ id: number; name: string }[]>([]);
  const [statuses, setStatuses] = useState<{ value: string; label: string }[]>([]);

  /**
   * Translates status code to user-friendly Ukrainian text
   */
  const translateStatus = (status: StationStatusType | string | undefined): string => {
    if (!status) return '';
    const statusOption = statuses.find(s => s.value === status);
    return statusOption ? statusOption.label : String(status);
  };

  const isOkkoStation = isOkkoStationCheck(editedStation?.brand || stationProfile?.brand || station.brand);

  useEffect(() => {
    setRegions(options?.regional_offices || []);
    setProvinces(options?.provinces || []);
    setDivisions(options?.divisions || []);
    setBrands(options?.brands || []);
    setStatuses(options?.statuses || []);
  }, [options]);

  useEffect(() => {
    if (!open) return;
    const source = stationProfile && typeof stationProfile === 'object' ? stationProfile : station || ({} as Station);
    const copy: Station = { ...source } as Station;
    initializeStationDefaults(copy);
    setEditedStation(copy);
    setOriginalStation({ ...copy });
  }, [open, stationProfile, station]);

  const initializeStationDefaults = (data: any): void => {
    if (!data) return;
    if (!data.unicode) data.unicode = generateUnicode();
    if (!data.additional_code) data.additional_code = '';
    if (data.latitude === undefined || data.latitude === null) data.latitude = '';
    if (data.longitude === undefined || data.longitude === null) data.longitude = '';
    if (!data.photo_url) data.photo_url = '';
    if (!data.google_maps_url) data.google_maps_url = '';
    if (!data.address_note) data.address_note = '';
    if (!data.regional_office) data.regional_office = '';
    if (!data.province) data.province = '';
    if (!data.division) data.division = '';

    if (data.start_date && typeof data.start_date === 'string') {
      data.start_date = new Date(data.start_date);
    } else if (!data.start_date) {
      data.start_date = null;
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    // ensure editedStation exists
    setEditedStation(prev => {
      const base = prev || (stationProfile as Station) || (station as Station) || ({} as Station);
      const updated = { ...base, [field]: value } as Station;

      // update changedFields by comparing to originalStation (fallback to base source)
      const original = originalStation || (stationProfile as Station) || (station as Station) || ({} as Station);
      setChangedFields(prevSet => {
        const newSet = new Set(prevSet);
        if (value !== (original as any)[field]) {
          newSet.add(field);
        } else {
          newSet.delete(field);
        }
        return newSet;
      });

      // Update validation errors for URL fields
      if (field === 'photo_url' || field === 'google_maps_url') {
        const isInvalidUrl = value && !/^https?:\/\/.+/.test(value);
        setValidationErrors((prev: Set<string>) => {
          const newSet = new Set(prev);
          if (isInvalidUrl) {
            newSet.add(field);
          } else {
            newSet.delete(field);
          }
          return newSet;
        });
      }

      return updated;
    });

    if (!isOkkoStation) {
      const requiredFields = [
        'regional_office',
        'province',
        'division',
        'address',
        'status',
        'start_date',
        'brand',
        'station_number',
        'latitude',
        'longitude'
      ];
      if (requiredFields.includes(field)) {
        const isEmpty = !value || (typeof value === 'string' && !value.trim());
        setValidationErrors((prev: Set<string>) => {
          const newSet = new Set(prev);
          if (isEmpty) {
            newSet.add(field);
          } else {
            newSet.delete(field);
          }
          return newSet;
        });
      }
    }
  };

  const handleEnterEditMode = () => {
    setIsEditMode(true);
  };

  const handleExitEditMode = (): void => {
    setIsEditMode(false);
    setChangedFields(new Set());
    setEditedStation(originalStation);
    setValidationErrors(new Set());
  };

  const handleClose = (): void => {
    if (isEditMode) {
      handleExitEditMode();
    }
    onClose();
  };

  const handleSave = async (): Promise<void> => {
    if (!editedStation) return;

    if (!isOkkoStation) {
      const emptyFields = requiredFields.filter(({ field }) => {
        const value = (editedStation as any)[field];
        return !value || (typeof value === 'string' && !value.trim());
      });

      if (emptyFields.length > 0) {
        const fieldNames = emptyFields.map(f => f.label).join(', ');
        setSnackbarMessage(`Помилка: Заповніть обов'язкові поля: ${fieldNames}`, 'error');
        setChangedFields(new Set(emptyFields.map(f => f.field)));
        setValidationErrors(new Set(emptyFields.map(f => f.field)));
        return;
      }
    }

    setSaving(true);
    try {
      const regional_office_id = findId(regions, editedStation?.regional_office);
      const province_id = findId(provinces, editedStation?.province);
      const division_id = findId(divisions, editedStation?.division);
      const brand_id = findId(brands, editedStation?.brand);

      const status = editedStation?.status ? getStatusKey(statuses, editedStation.status) : undefined;

      const payload: UpdateStationRequest = {};
      if (brand_id !== undefined) payload.brand_id = brand_id;
      if (regional_office_id !== undefined) payload.regional_office_id = regional_office_id;
      if (province_id !== undefined) payload.province_id = province_id;
      if (division_id !== undefined) payload.division_id = division_id;
      if (editedStation?.address) payload.address = editedStation.address;
      if (status !== undefined) payload.status = status;
      if (editedStation?.start_date instanceof Date) payload.start_date = editedStation.start_date;
      if (editedStation?.station_number) payload.station_number = editedStation.station_number;
      if (editedStation?.latitude !== undefined) payload.latitude = editedStation.latitude;
      if (editedStation?.longitude !== undefined) payload.longitude = editedStation.longitude;
      payload.additional_code = editedStation.additional_code;
      payload.photo_url = editedStation.photo_url;
      payload.address_note = editedStation.address_note;
      payload.google_maps_url = editedStation.google_maps_url;

      await updateStationProfile({ unicode: String(editedStation.unicode), data: payload }).unwrap();

      setSnackbarMessage('Профіль АЗК було успішно оновлено', 'success');
      setSaving(false);
      setIsEditMode(false);
      setChangedFields(new Set());
    } catch (error: any) {
      setSaving(false);
      const message = error?.data?.message || error.message || 'Невідома помилка';
      setSnackbarMessage(`Помилка збереження профілю: ${message}`, 'error');
    }
  };

  const handleDelete = (): void => {
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setDeleting(true);
    try {
      const unicode = String(stationProfile?.unicode || editedStation?.unicode || station.unicode || '');
      if (!unicode) throw new Error('Missing station identifier');
      await deleteStation(unicode).unwrap();
      setDeleteConfirmOpen(false);
      setDeleting(false);
      onClose();
      setSnackbarMessage('Конкурент успішно видалено', 'success');
    } catch (error: any) {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      const errorMessage =
        'Помилка видалення конкурента: ' + (error.response?.data?.message || error.message || 'Невідома помилка');
      setSnackbarMessage(errorMessage, 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={false} PaperProps={{ className: 'station-dialog-paper' }}>
      <ProfileHeader isOkkoStation={isOkkoStation} onClose={handleClose} />

      <Box className={`station-profile-dialog-content ${readOnly ? 'read-only' : ''}`}>
        {isStationProfileLoading || !editedStation ? (
          <Box className="station-profile-loading-container">
            <Box className="station-profile-loading-spinner">⟳</Box>
          </Box>
        ) : (
          <>
            <ProfileTags stationData={editedStation} />
            <ProfileForm
              stationData={editedStation}
              isEditMode={isEditMode}
              isOkkoStation={isOkkoStation}
              changedFields={changedFields}
              validationErrors={validationErrors}
              regions={regions}
              provinces={provinces}
              divisions={divisions}
              brands={brands}
              statuses={statuses}
              onFieldChange={handleFieldChange}
              translateStatus={translateStatus}
              formatDate={date => {
                if (!date) return '-';
                if (date instanceof Date) return date.toLocaleDateString('uk-UA');
                return date;
              }}
            />
            <ProfileMap stationData={originalStation} />
          </>
        )}
      </Box>

      {editedStation && !readOnly && (
        <ProfileActions
          isEditMode={isEditMode}
          isOkkoStation={isOkkoStation}
          changedFieldsCount={changedFields.size}
          validationErrors={validationErrors}
          saving={saving}
          deleting={deleting}
          onEnterEditMode={handleEnterEditMode}
          onExitEditMode={handleExitEditMode}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}

      {!readOnly && (
        <DeleteConfirmDialog
          open={deleteConfirmOpen}
          brand={editedStation?.brand || stationProfile?.brand || ''}
          address={editedStation?.address || stationProfile?.address || ''}
          deleting={deleting}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Dialog>
  );
}

export default StationProfile;
